import React, { useState, useRef, useEffect } from 'react';
import { Upload, Play, Pause, Download, Loader, Music, Scissors, RefreshCw, Volume2, VolumeX, AlertCircle } from 'lucide-react';

// Configuration de l'API backend
// En production, utilise l'URL du backend PythonAnywhere
// En développement, utilise localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Extension du type Window pour webkitAudioContext (support Safari)
declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

// Types TypeScript
interface StemState {
  name: string;
  enabled: boolean;
  volume: number;
  url: string;
}

interface StemsState {
  [key: string]: StemState;
}

interface Section {
  name: string;
  start: number;
  end: number;
}

interface AudioFile {
  type: 'file' | 'youtube';
  file?: File;
  url?: string;
}

// Hook personnalisé pour la gestion de l'audio
const useAudioPlayer = (stems: StemsState) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBuffersRef = useRef<{ [key: string]: AudioBuffer }>({});
  const sourceNodesRef = useRef<{ [key: string]: AudioBufferSourceNode }>({});
  const gainNodesRef = useRef<{ [key: string]: GainNode }>({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const startTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    // Initialiser le contexte audio
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    audioContextRef.current = new AudioContextClass();
    
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const loadAudioBuffer = async (stemKey: string, audioData: Blob) => {
    if (!audioContextRef.current) return;
    
    try {
      const arrayBuffer = await audioData.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      audioBuffersRef.current[stemKey] = audioBuffer;
      
      if (duration === 0) {
        setDuration(audioBuffer.duration);
      }
    } catch (error) {
      console.error(`Erreur lors du chargement du stem ${stemKey}:`, error);
    }
  };

  const play = (loopStart: number | null = null, loopEnd: number | null = null) => {
    if (!audioContextRef.current || isPlaying) return;

    const ctx = audioContextRef.current;
    const startOffset = currentTime;

    Object.entries(audioBuffersRef.current).forEach(([stemKey, buffer]) => {
      const source = ctx.createBufferSource();
      const gainNode = ctx.createGain();
      
      source.buffer = buffer;
      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      // Appliquer le volume actuel
      const stemState = stems[stemKey];
      gainNode.gain.value = stemState?.enabled ? (stemState?.volume || 1) : 0;
      
      // Gérer la boucle si définie
      if (loopStart !== null && loopEnd !== null) {
        source.loop = true;
        source.loopStart = loopStart;
        source.loopEnd = loopEnd;
      }
      
      source.start(0, startOffset);
      
      sourceNodesRef.current[stemKey] = source;
      gainNodesRef.current[stemKey] = gainNode;
    });

    startTimeRef.current = ctx.currentTime - startOffset;
    setIsPlaying(true);
    updateCurrentTime();
  };

  const pause = () => {
    Object.values(sourceNodesRef.current).forEach((source: AudioBufferSourceNode) => {
      try {
        source.stop();
      } catch (e) {
        // Source déjà arrêtée
      }
    });
    
    sourceNodesRef.current = {};
    gainNodesRef.current = {};
    setIsPlaying(false);
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const updateCurrentTime = () => {
    if (!audioContextRef.current || !isPlaying) return;
    
    const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
    setCurrentTime(Math.min(elapsed, duration));
    
    if (elapsed >= duration) {
      pause();
      setCurrentTime(0);
    } else {
      animationFrameRef.current = requestAnimationFrame(updateCurrentTime);
    }
  };

  const seek = (time: number) => {
    const wasPlaying = isPlaying;
    if (wasPlaying) {
      pause();
    }
    setCurrentTime(Math.max(0, Math.min(time, duration)));
    if (wasPlaying) {
      play();
    }
  };

  const setStemVolume = (stemKey: string, volume: number) => {
    if (gainNodesRef.current[stemKey]) {
      gainNodesRef.current[stemKey].gain.value = volume;
    }
  };

  return {
    loadAudioBuffer,
    play,
    pause,
    seek,
    setStemVolume,
    isPlaying,
    currentTime,
    duration
  };
};

// Composant principal
const StemLoopApp = () => {
  const [currentView, setCurrentView] = useState<'upload' | 'process' | 'player'>('upload');
  const [audioFile, setAudioFile] = useState<AudioFile | null>(null);
  const [stems, setStems] = useState<StemsState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {error && (
          <div className="mb-6 bg-red-900 bg-opacity-30 border border-red-500 border-opacity-50 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-300 mb-1">Erreur</h4>
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          </div>
        )}

        {currentView === 'upload' && (
          <UploadView 
            onFileSelect={(file) => {
              setAudioFile(file);
              setError(null);
              setCurrentView('process');
            }}
          />
        )}
        {currentView === 'process' && audioFile && (
          <ProcessView 
            audioFile={audioFile}
            onProcessComplete={(processedStems, id) => {
              setStems(processedStems);
              setJobId(id);
              setCurrentView('player');
            }}
            onError={(err) => {
              setError(err);
              setCurrentView('upload');
            }}
            onBack={() => {
              setError(null);
              setCurrentView('upload');
            }}
          />
        )}
        {currentView === 'player' && stems && (
          <PlayerView 
            stems={stems}
            audioFile={audioFile}
            jobId={jobId}
            onBack={() => {
              setError(null);
              setCurrentView('upload');
            }}
          />
        )}
      </main>
    </div>
  );
};

// Composant Header
const Header = () => (
  <header className="bg-black bg-opacity-30 backdrop-blur-md border-b border-white border-opacity-10">
    <div className="container mx-auto px-4 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Music className="w-8 h-8 text-purple-400" />
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            StemLoop Pro
          </h1>
        </div>
        <div className="text-sm text-gray-300">
          Isolez et bouclez vos instruments
        </div>
      </div>
    </div>
  </header>
);

// Composant Upload
interface UploadViewProps {
  onFileSelect: (file: AudioFile) => void;
}

const UploadView = ({ onFileSelect }: UploadViewProps) => {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (file.type.startsWith('audio/') || file.name.endsWith('.mp3') || file.name.endsWith('.wav')) {
      onFileSelect({ type: 'file', file });
    } else {
      alert('Veuillez sélectionner un fichier audio valide (MP3, WAV, etc.)');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleYoutubeSubmit = () => {
    if (!youtubeUrl.trim()) {
      alert('Veuillez entrer une URL YouTube valide');
      return;
    }
    
    if (!youtubeUrl.includes('youtube.com') && !youtubeUrl.includes('youtu.be')) {
      alert('URL YouTube invalide');
      return;
    }
    
    setLoading(true);
    onFileSelect({ type: 'youtube', url: youtubeUrl });
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-bold">Commencez votre pratique musicale</h2>
        <p className="text-gray-300 text-lg">
          Téléchargez un fichier audio ou collez un lien YouTube
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
            ${dragActive ? 'border-purple-400 bg-purple-400 bg-opacity-10' : 'border-gray-600 hover:border-purple-500'}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.flac,.ogg,.m4a"
            onChange={handleFileInput}
            className="hidden"
          />
          <Upload className="w-16 h-16 mx-auto mb-4 text-purple-400" />
          <h3 className="text-xl font-semibold mb-2">Télécharger un fichier</h3>
          <p className="text-gray-400">
            Glissez-déposez ou cliquez pour sélectionner
          </p>
          <p className="text-sm text-gray-500 mt-2">
            MP3, WAV, FLAC, OGG, M4A
          </p>
        </div>

        <div className="border-2 border-gray-600 rounded-xl p-8 hover:border-purple-500 transition-all">
          <Music className="w-16 h-16 mx-auto mb-4 text-pink-400" />
          <h3 className="text-xl font-semibold mb-4 text-center">Depuis YouTube</h3>
          <input
            type="text"
            placeholder="https://youtube.com/watch?v=..."
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleYoutubeSubmit()}
            className="w-full px-4 py-3 bg-white bg-opacity-10 border border-gray-600 rounded-lg 
              focus:outline-none focus:border-purple-400 transition-all"
          />
          <button
            onClick={handleYoutubeSubmit}
            disabled={loading}
            className="w-full mt-4 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 
              rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Extraction...' : 'Extraire l\'audio'}
          </button>
        </div>
      </div>

      <div className="bg-blue-900 bg-opacity-30 border border-blue-500 border-opacity-30 rounded-xl p-6">
        <h4 className="font-semibold mb-2 flex items-center gap-2">
          <Music className="w-5 h-5" />
          Fonctionnalités
        </h4>
        <ul className="space-y-2 text-gray-300">
          <li>• Séparez les instruments (batterie, basse, guitare, voix)</li>
          <li>• Isolez ou supprimez n'importe quel instrument</li>
          <li>• Détection automatique des sections à boucler</li>
          <li>• Contrôle du tempo et de la hauteur</li>
          <li>• Export en haute qualité</li>
        </ul>
      </div>
    </div>
  );
};

// Composant Process
interface ProcessViewProps {
  audioFile: AudioFile;
  onProcessComplete: (stems: StemsState, id: string) => void;
  onError: (error: string) => void;
  onBack: () => void;
}

const ProcessView = ({ audioFile, onProcessComplete, onError, onBack }: ProcessViewProps) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('Initialisation...');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const startProcessing = async () => {
    try {
      const formData = new FormData();
      
      if (audioFile.type === 'youtube') {
        formData.append('youtube_url', audioFile.url || '');
      } else {
        formData.append('audio_file', audioFile.file!);
      }

      setCurrentStep('Envoi du fichier au serveur...');
      
      const response = await fetch(`${API_BASE_URL}/process`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du fichier');
      }

      const data = await response.json();
      
      // Démarrer le polling pour vérifier le statut
      pollJobStatus(data.job_id);
      
    } catch (error) {
      console.error('Erreur:', error);
      onError('Erreur lors du traitement. Assurez-vous que le serveur backend est démarré.');
    }
  };

  useEffect(() => {
    startProcessing();
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollJobStatus = (id: string) => {
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/status/${id}`);
        const data = await response.json();
        
        setProgress(data.progress || 0);
        setCurrentStep(data.status || 'Traitement en cours...');
        
        if (data.status === 'completed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          
          // Récupérer les stems
          const stemsData = {
            drums: { name: 'Batterie', enabled: true, volume: 1, url: `${API_BASE_URL}/stems/${id}/drums` },
            bass: { name: 'Basse', enabled: true, volume: 1, url: `${API_BASE_URL}/stems/${id}/bass` },
            other: { name: 'Autres (Guitare)', enabled: true, volume: 1, url: `${API_BASE_URL}/stems/${id}/other` },
            vocals: { name: 'Voix', enabled: true, volume: 1, url: `${API_BASE_URL}/stems/${id}/vocals` }
          };
          
          onProcessComplete(stemsData, id);
        } else if (data.status === 'error') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
          onError(data.error || 'Erreur lors du traitement');
        }
        
      } catch (error) {
        console.error('Erreur lors de la vérification du statut:', error);
      }
    }, 2000);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <button
        onClick={onBack}
        className="text-gray-400 hover:text-white transition-colors"
      >
        ← Retour
      </button>

      <div className="bg-black bg-opacity-30 backdrop-blur-md border border-white border-opacity-10 rounded-xl p-8">
        <div className="text-center space-y-6">
          <Loader className="w-16 h-16 mx-auto animate-spin text-purple-400" />
          
          <div>
            <h3 className="text-2xl font-bold mb-2">Traitement en cours</h3>
            <p className="text-gray-400">{currentStep}</p>
          </div>

          <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          <p className="text-sm text-gray-400">
            {Math.round(progress)}% complété
          </p>
        </div>
      </div>

      <div className="bg-yellow-900 bg-opacity-20 border border-yellow-500 border-opacity-30 rounded-xl p-6">
        <p className="text-sm text-yellow-200">
          <strong>Note :</strong> La séparation des stems peut prendre 2-5 minutes selon la longueur du morceau. 
          Le traitement utilise Demucs avec des modèles d'IA avancés.
        </p>
      </div>
    </div>
  );
};

// Composant Player
interface PlayerViewProps {
  stems: StemsState;
  audioFile: AudioFile | null;
  jobId: string | null;
  onBack: () => void;
}

const PlayerView = ({ stems, audioFile, jobId, onBack }: PlayerViewProps) => {
  const [stemStates, setStemStates] = useState<StemsState>(stems);
  const [loopStart, setLoopStart] = useState<number | null>(null);
  const [loopEnd, setLoopEnd] = useState<number | null>(null);
  const [detectedSections, setDetectedSections] = useState<Section[]>([]);
  const [loadingStems, setLoadingStems] = useState(true);
  
  const audioPlayer = useAudioPlayer(stemStates);

  useEffect(() => {
    loadAllStems();
    fetchDetectedSections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllStems = async () => {
    setLoadingStems(true);
    try {
      for (const [key, stem] of Object.entries(stems)) {
        const response = await fetch(stem.url);
        const blob = await response.blob();
        await audioPlayer.loadAudioBuffer(key, blob);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des stems:', error);
    }
    setLoadingStems(false);
  };

  const fetchDetectedSections = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/sections/${jobId}`);
      const data = await response.json();
      if (data.sections) {
        setDetectedSections(data.sections);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des sections:', error);
    }
  };

  const toggleStem = (stemKey: string) => {
    setStemStates(prev => {
      const newState = {
        ...prev,
        [stemKey]: { ...prev[stemKey], enabled: !prev[stemKey].enabled }
      };
      
      const newVolume = newState[stemKey].enabled ? newState[stemKey].volume : 0;
      audioPlayer.setStemVolume(stemKey, newVolume);
      
      return newState;
    });
  };

  const setStemVolume = (stemKey: string, volume: number) => {
    setStemStates(prev => ({
      ...prev,
      [stemKey]: { ...prev[stemKey], volume }
    }));
    
    if (stemStates[stemKey].enabled) {
      audioPlayer.setStemVolume(stemKey, volume);
    }
  };

  const handlePlayPause = () => {
    if (audioPlayer.isPlaying) {
      audioPlayer.pause();
    } else {
      audioPlayer.play(loopStart, loopEnd);
    }
  };

  const setLoop = (start: number, end: number) => {
    setLoopStart(start);
    setLoopEnd(end);
    
    if (audioPlayer.isPlaying) {
      audioPlayer.pause();
      audioPlayer.play(start, end);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const downloadMix = async () => {
    try {
      const enabledStems = Object.entries(stemStates)
        .filter(([_, stem]) => stem.enabled)
        .map(([key, _]) => key);
      
      const response = await fetch(`${API_BASE_URL}/mix/${jobId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stems: enabledStems })
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mix.mp3';
      a.click();
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
    }
  };

  if (loadingStems) {
    return (
      <div className="text-center py-12">
        <Loader className="w-12 h-12 mx-auto animate-spin text-purple-400 mb-4" />
        <p className="text-gray-300">Chargement des stems...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="text-gray-400 hover:text-white transition-colors"
      >
        ← Nouveau morceau
      </button>

      {/* Timeline et contrôles */}
      <div className="bg-black bg-opacity-30 backdrop-blur-md border border-white border-opacity-10 rounded-xl p-6">
        <h3 className="text-xl font-semibold mb-4">Timeline</h3>
        
        <div className="relative h-32 bg-gray-800 rounded-lg overflow-hidden mb-4 cursor-pointer"
             onClick={(e) => {
               const rect = e.currentTarget.getBoundingClientRect();
               const x = e.clientX - rect.left;
               const percentage = x / rect.width;
               audioPlayer.seek(percentage * audioPlayer.duration);
             }}>
          
          {/* Marqueurs de boucle */}
          {loopStart !== null && loopEnd !== null && (
            <div
              className="absolute top-0 bottom-0 bg-green-500 bg-opacity-20 border-l-2 border-r-2 border-green-500"
              style={{
                left: `${(loopStart / audioPlayer.duration) * 100}%`,
                width: `${((loopEnd - loopStart) / audioPlayer.duration) * 100}%`
              }}
            />
          )}
          
          {/* Playhead */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg"
            style={{ left: `${(audioPlayer.currentTime / audioPlayer.duration) * 100}%` }}
          />
          
          <div className="absolute inset-0 flex items-center justify-center text-gray-500">
            Cliquez pour naviguer
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-400">{formatTime(audioPlayer.currentTime)}</span>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => audioPlayer.seek(Math.max(0, audioPlayer.currentTime - 10))}
              className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
            >
              ⏪
            </button>
            
            <button
              onClick={handlePlayPause}
              className="p-4 bg-purple-500 hover:bg-purple-600 rounded-full transition-colors"
            >
              {audioPlayer.isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
            </button>
            
            <button
              onClick={() => audioPlayer.seek(Math.min(audioPlayer.duration, audioPlayer.currentTime + 10))}
              className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
            >
              ⏩
            </button>
          </div>
          
          <span className="text-sm text-gray-400">{formatTime(audioPlayer.duration)}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Contrôle des stems */}
        <div className="bg-black bg-opacity-30 backdrop-blur-md border border-white border-opacity-10 rounded-xl p-6">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Scissors className="w-5 h-5" />
            Contrôle des Stems
          </h3>
          
          <div className="space-y-4">
            {Object.entries(stemStates).map(([key, stem]) => (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{stem.name}</span>
                  <button
                    onClick={() => toggleStem(key)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      stem.enabled
                        ? 'bg-green-500 hover:bg-green-600'
                        : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    {stem.enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                </div>
                
                {stem.enabled && (
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={stem.volume}
                    onChange={(e) => setStemVolume(key, parseFloat(e.target.value))}
                    className="w-full"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sections détectées */}
        <div className="bg-black bg-opacity-30 backdrop-blur-md border border-white border-opacity-10 rounded-xl p-6">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Sections détectées
          </h3>
          
          <div className="space-y-2">
            {detectedSections.length > 0 ? (
              detectedSections.map((section, idx) => (
                <button
                  key={idx}
                  onClick={() => setLoop(section.start, section.end)}
                  className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-left"
                >
                  <div>
                    <div className="font-medium">{section.name}</div>
                    <div className="text-sm text-gray-400">
                      {formatTime(section.start)} - {formatTime(section.end)}
                    </div>
                  </div>
                  <div className="text-purple-400">Boucler →</div>
                </button>
              ))
            ) : (
              <p className="text-gray-400 text-sm">Analyse des sections en cours...</p>
            )}
          </div>

          {loopStart !== null && loopEnd !== null && (
            <div className="mt-4 p-3 bg-green-900 bg-opacity-30 border border-green-500 border-opacity-30 rounded-lg">
              <p className="text-sm text-green-200">
                ✓ Boucle : {formatTime(loopStart)} - {formatTime(loopEnd)}
              </p>
              <button
                onClick={() => {
                  setLoopStart(null);
                  setLoopEnd(null);
                }}
                className="mt-2 text-sm text-green-400 hover:text-green-300"
              >
                Désactiver
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Options de téléchargement */}
      <div className="bg-black bg-opacity-30 backdrop-blur-md border border-white border-opacity-10 rounded-xl p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Download className="w-5 h-5" />
          Télécharger
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <button 
            onClick={downloadMix}
            className="px-4 py-3 bg-purple-500 hover:bg-purple-600 rounded-lg transition-colors font-semibold"
          >
            Mix actuel
          </button>
          {Object.entries(stemStates).map(([key, stem]) => (
            <button
              key={key}
              onClick={() => window.open(stem.url, '_blank')}
              className="px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              {stem.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const App = StemLoopApp;
export default App;